/**
 * ContactDetailPage — vault_business_contacts + fias_leads_contact_extension
 * + relationships sub-section + activity timeline.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Archive, Sparkles } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsContact,
  useFiasLeadsContacts,
  useUpdateFiasLeadsContact,
  useArchiveFiasLeadsContact,
  useFiasLeadsCompany,
  useFiasLeadsCompanyRefs,
  useFiasLeadsRelationships,
  useCreateFiasLeadsRelationship,
  useDeleteFiasLeadsRelationship,
} from '../hooks/useFiasLeads';
import type { FiasLeadsContactRelationshipType, FiasLeadsContactRelationshipDto } from '@/lib/api';
import { api } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { SkeletonBlock } from '../components/Skeletons';
import { ActivitiesTimelinePanel } from '../components/ActivitiesTimelinePanel';
import { CreateDealButton } from '../components/CreateDealButton';

interface Props {
  holderId: string;
}

const RELATIONSHIP_TYPES: FiasLeadsContactRelationshipType[] = [
  'colleague',
  'manager',
  'reports_to',
  'mentor',
  'spouse',
  'partner',
  'friend',
  'sibling',
  'parent',
  'child',
  'client',
  'vendor',
  'referral',
  'other',
];

export function ContactDetailPage({ holderId }: Props) {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const contactQuery = useFiasLeadsContact(holderId, contactId ?? null);
  const updateContact = useUpdateFiasLeadsContact(holderId);
  const archiveContact = useArchiveFiasLeadsContact(holderId);
  // Refs, not the full list: complete + alphabetized. The full list's
  // 100-row default cap made a linked company outside the window render
  // as an UNSELECTED select — this swap fixes that for active
  // companies; an ARCHIVED linked company (refs exclude archived) is
  // handled below by injecting it as an extra option.
  const companyRefsQuery = useFiasLeadsCompanyRefs(holderId);
  const savedCompanyId = contactQuery.data?.contact.companyId ?? null;
  const savedCompanyInRefs =
    !savedCompanyId || (companyRefsQuery.data ?? []).some((r) => r.companyId === savedCompanyId);
  // Only fetched when the saved link points outside the refs (archived).
  const savedCompanyQuery = useFiasLeadsCompany(
    holderId,
    savedCompanyInRefs ? null : savedCompanyId,
  );
  const relationshipsQuery = useFiasLeadsRelationships(holderId, contactId ?? null);
  const createRel = useCreateFiasLeadsRelationship(holderId);
  const deleteRel = useDeleteFiasLeadsRelationship(holderId);

  const [edit, setEdit] = useState({
    firstName: '',
    lastName: '',
    primaryEmail: '',
    primaryPhone: '',
    title: '',
    lifecycleStage: '',
    companyId: '',
    notes: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    if (contactQuery.data?.contact) {
      const c = contactQuery.data.contact;
      setEdit({
        firstName: c.firstName,
        lastName: c.lastName ?? '',
        primaryEmail: c.primaryEmail ?? '',
        primaryPhone: c.primaryPhone ?? '',
        title: c.title ?? '',
        lifecycleStage: c.lifecycleStage ?? '',
        companyId: c.companyId ?? '',
        notes: c.notes ?? '',
      });
      setIsDirty(false);
    }
  }, [contactQuery.data]);

  const handleSave = async () => {
    if (!contactId) return;
    await updateContact.mutateAsync({
      contactId,
      input: {
        firstName: edit.firstName,
        lastName: edit.lastName || null,
        primaryEmail: edit.primaryEmail || null,
        primaryPhone: edit.primaryPhone || null,
        title: edit.title || null,
        lifecycleStage: edit.lifecycleStage || null,
        companyId: edit.companyId || null,
        notes: edit.notes || null,
      },
    });
    setIsDirty(false);
  };

  const handleArchive = async () => {
    if (!contactId) return;
    if (
      !confirm('Archive this contact? You can restore it from the Contacts list — Show archived.')
    )
      return;
    await archiveContact.mutateAsync(contactId);
    navigate(`${ARCHE_PATH}/contacts`);
  };

  const handleScoreLead = async () => {
    if (!contactId) return;
    setScoring(true);
    try {
      await api.fiasLeadsScoreLead(holderId, contactId);
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contact(holderId, contactId) });
    } catch (err) {
      // 501 from the AI endpoint is expected today — provider
      // integration is a tracked follow-up. Surface the message so
      // the user isn't confused by silent failure.
      alert((err as Error).message);
    } finally {
      setScoring(false);
    }
  };

  const handleSummarize = async () => {
    if (!contactId) return;
    setSummarizing(true);
    try {
      await api.fiasLeadsSummarize(holderId, 'contact', contactId);
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contact(holderId, contactId) });
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSummarizing(false);
    }
  };

  if (contactQuery.isLoading) return <SkeletonBlock lines={8} />;
  if (contactQuery.isError || !contactQuery.data?.contact) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link
          to={`${ARCHE_PATH}/contacts`}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Contacts
        </Link>
        <p className="mt-4 text-destructive">Contact not found.</p>
      </div>
    );
  }

  const c = contactQuery.data.contact;
  const ext = contactQuery.data.extension;
  const relationships = relationshipsQuery.data ?? [];
  const contactName = `${c.firstName} ${c.lastName ?? ''}`.trim();

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <Link
        to={`${ARCHE_PATH}/contacts`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Contacts
      </Link>

      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {c.firstName} {c.lastName ?? ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            {c.title ?? '—'} {c.primaryEmail ? `· ${c.primaryEmail}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <CreateDealButton holderId={holderId} contactId={contactId!} contactName={contactName} />
          <button
            onClick={handleScoreLead}
            disabled={scoring}
            className="inline-flex items-center gap-2 text-sm rounded border border-border px-3 py-1.5 hover:bg-muted/30"
          >
            <Sparkles className="h-4 w-4" />
            {scoring ? 'Scoring…' : 'Score lead'}
          </button>
          <button
            onClick={handleSummarize}
            disabled={summarizing}
            className="inline-flex items-center gap-2 text-sm rounded border border-border px-3 py-1.5 hover:bg-muted/30"
          >
            <Sparkles className="h-4 w-4" />
            {summarizing ? 'Summarizing…' : 'AI summary'}
          </button>
          <button
            onClick={handleArchive}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive"
            disabled={archiveContact.isPending}
          >
            <Archive className="h-4 w-4" />
            Archive
          </button>
        </div>
      </header>

      <div className="rounded border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Details</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="First name"
            value={edit.firstName}
            onChange={(v) => {
              setEdit((p) => ({ ...p, firstName: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Last name"
            value={edit.lastName}
            onChange={(v) => {
              setEdit((p) => ({ ...p, lastName: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Email"
            value={edit.primaryEmail}
            onChange={(v) => {
              setEdit((p) => ({ ...p, primaryEmail: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Phone"
            value={edit.primaryPhone}
            onChange={(v) => {
              setEdit((p) => ({ ...p, primaryPhone: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Title"
            value={edit.title}
            onChange={(v) => {
              setEdit((p) => ({ ...p, title: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Lifecycle stage"
            value={edit.lifecycleStage}
            onChange={(v) => {
              setEdit((p) => ({ ...p, lifecycleStage: v }));
              setIsDirty(true);
            }}
          />
          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs text-muted-foreground">Company</span>
            {/* Imported-but-unlinked provenance: show the raw name the
                import carried, so it isn't silently lost while no
                matching company exists yet. Picking one below links it
                (the server clears the raw text on a manual change). */}
            {!edit.companyId && contactQuery.data?.contact.companyName && (
              <p className="text-sm">
                {contactQuery.data.contact.companyName}{' '}
                <span className="text-xs text-muted-foreground">
                  (imported — not linked yet; it links automatically when this company is created or
                  imported, or pick one below)
                </span>
              </p>
            )}
            <select
              value={edit.companyId}
              onChange={(e) => {
                setEdit((p) => ({ ...p, companyId: e.target.value }));
                setIsDirty(true);
              }}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">— None —</option>
              {!savedCompanyInRefs && savedCompanyId && (
                <option value={savedCompanyId}>
                  {savedCompanyQuery.data?.name ?? 'Current company'} (archived)
                </option>
              )}
              {(companyRefsQuery.data ?? []).map((co) => (
                <option key={co.companyId} value={co.companyId}>
                  {co.name}
                </option>
              ))}
            </select>
          </label>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">Notes</label>
            <textarea
              value={edit.notes}
              onChange={(e) => {
                setEdit((p) => ({ ...p, notes: e.target.value }));
                setIsDirty(true);
              }}
              rows={3}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={updateContact.isPending}
            className="mt-3 rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm"
          >
            {updateContact.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>

      {(ext?.leadScore != null || ext?.aiSummary || ext?.lastContactedAt) && (
        <div className="rounded border border-border bg-card p-4">
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Leads extension</h2>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">Lead score</dt>
              <dd className="font-medium">
                {ext?.leadScore != null ? ext.leadScore.toFixed(0) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Last contacted</dt>
              <dd className="font-medium">
                {ext?.lastContactedAt ? new Date(ext.lastContactedAt).toLocaleDateString() : '—'}
              </dd>
            </div>
          </dl>
          {ext?.aiSummary && (
            <div className="mt-3 rounded bg-muted/30 p-3 text-sm">
              <span className="text-xs uppercase text-muted-foreground">AI summary</span>
              <p className="mt-1">{ext.aiSummary}</p>
            </div>
          )}
        </div>
      )}

      <RelationshipsSection
        relationships={relationships}
        relationshipTypes={RELATIONSHIP_TYPES}
        contactId={contactId!}
        holderId={holderId}
        onCreate={(input) => createRel.mutateAsync(input)}
        onDelete={(id) => deleteRel.mutateAsync(id)}
        isPending={createRel.isPending || deleteRel.isPending}
      />

      <ActivitiesTimelinePanel
        holderId={holderId}
        regardingType="vault_contact"
        regardingId={contactId!}
      />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}

interface RelationshipsSectionProps {
  relationships: FiasLeadsContactRelationshipDto[];
  relationshipTypes: FiasLeadsContactRelationshipType[];
  contactId: string;
  holderId: string;
  onCreate: (input: {
    fromContactId: string;
    toContactId: string;
    relationshipType: FiasLeadsContactRelationshipType;
    notes?: string;
  }) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  isPending: boolean;
}

function RelationshipsSection({
  relationships,
  relationshipTypes,
  contactId,
  holderId,
  onCreate,
  onDelete,
  isPending,
}: RelationshipsSectionProps) {
  const allContactsQuery = useFiasLeadsContacts(holderId);
  const [toContactId, setToContactId] = useState('');
  const [relType, setRelType] = useState<FiasLeadsContactRelationshipType>('colleague');
  const [showAdd, setShowAdd] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!toContactId) return;
    await onCreate({
      fromContactId: contactId,
      toContactId,
      relationshipType: relType,
    });
    setToContactId('');
    setShowAdd(false);
  };

  const otherContacts = (allContactsQuery.data ?? []).filter((c) => c.contactId !== contactId);

  return (
    <div className="rounded border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-muted-foreground">
          Relationships ({relationships.length})
        </h2>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {showAdd ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="flex flex-wrap gap-2 mb-3">
          <select
            value={toContactId}
            onChange={(e) => setToContactId(e.target.value)}
            className="flex-1 min-w-[200px] rounded border border-border bg-background px-3 py-2 text-sm"
            required
          >
            <option value="">Select contact…</option>
            {otherContacts.map((c) => (
              <option key={c.contactId} value={c.contactId}>
                {c.firstName} {c.lastName ?? ''}
              </option>
            ))}
          </select>
          <select
            value={relType}
            onChange={(e) => setRelType(e.target.value as FiasLeadsContactRelationshipType)}
            className="rounded border border-border bg-background px-3 py-2 text-sm"
          >
            {relationshipTypes.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!toContactId || isPending}
            className="rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            Add
          </button>
        </form>
      )}

      {relationships.length === 0 ? (
        <p className="text-sm text-muted-foreground">No relationships.</p>
      ) : (
        <ul className="divide-y divide-border">
          {relationships.map((r) => {
            const other = (allContactsQuery.data ?? []).find((c) => c.contactId === r.toContactId);
            return (
              <li key={r.relationshipId} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <span className="rounded bg-muted/40 px-2 py-0.5 text-xs">
                    {r.relationshipType}
                  </span>
                  <span className="ml-2">
                    {other ? `${other.firstName} ${other.lastName ?? ''}`.trim() : r.toContactId}
                  </span>
                </div>
                <button
                  onClick={() => onDelete(r.relationshipId)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
